import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import type { DocTitle } from '@blocksuite/affine-fragment-doc-title';
import {
  NOTE_MIN_HEIGHT,
  NoteBlockSchema,
  NoteDisplayMode,
} from '@blocksuite/affine-model';
import { focusTextModel } from '@blocksuite/affine-rich-text';
import {
  EDGELESS_BLOCK_CHILD_PADDING,
  STREAMLAIN_NOTE_LAYOUT_READY_ATTR,
} from '@blocksuite/affine-shared/consts';
import { TelemetryProvider } from '@blocksuite/affine-shared/services';
import {
  handleNativeRangeAtPoint,
  stopPropagation,
} from '@blocksuite/affine-shared/utils';
import {
  almostEqual,
  Bound,
  type SerializedXYWH,
} from '@blocksuite/global/gfx';
import { toGfxBlockComponent } from '@blocksuite/std';
import {
  type BoxSelectionContext,
  type DragEndContext,
  type DragMoveContext,
  GfxViewInteractionExtension,
  measureGfxPerformance,
} from '@blocksuite/std/gfx';
import { html, nothing, type PropertyValues } from 'lit';
import { query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';
import clamp from 'lodash-es/clamp';

import { MoreIndicator } from './components/more-indicator';
import { NoteConfigExtension } from './config';
import { resolveEdgelessNoteStyle } from './edgeless-note-style';
import { NoteBlockComponent } from './note-block';
import { ACTIVE_NOTE_EXTRA_PADDING } from './note-edgeless-block.css';
import * as styles from './note-edgeless-block.css';
import {
  DATABASE_INITIAL_LOAD_READY_ATTR,
  hasPendingEmbeddedNoteLayout,
  hasPotentialPendingEmbeddedNoteLayoutModel,
} from './note-layout-readiness';
import { getPersistedNoteBound } from './persisted-note-xywh';

export const AFFINE_EDGELESS_NOTE = 'affine-edgeless-note';

export class EdgelessNoteBlockComponent extends toGfxBlockComponent(
  NoteBlockComponent
) {
  private _liveRenderingTransientXYWH: SerializedXYWH | null = null;

  private _hasPendingEmbeddedLayout = false;

  private _embeddedLayoutDomSynced = false;

  private get _isShowCollapsedContent() {
    return (
      !!this.model.props.edgeless?.collapse &&
      this.selected$.value &&
      !this._dragging &&
      (this._isResizing || this._isHover || this._editing)
    );
  }

  private get _dragging() {
    return this._isHover && this.gfx.tool.dragging$.value;
  }

  private _collapsedContent() {
    if (!this._isShowCollapsedContent) {
      return nothing;
    }

    const { xywh, edgeless } = this.model.props;
    const { borderSize } = resolveEdgelessNoteStyle(edgeless);

    const extraPadding = this._editing ? ACTIVE_NOTE_EXTRA_PADDING : 0;
    const extraBorder = this._editing ? borderSize : 0;
    const bound = Bound.deserialize(xywh);
    const scale = edgeless?.scale ?? 1;
    const width = bound.w / scale + extraPadding * 2 + extraBorder;
    const height = bound.h / scale;

    const rect = this._noteContent?.getBoundingClientRect();
    if (!rect) return nothing;

    const zoom = this.gfx.viewport.zoom;
    this._noteFullHeight =
      rect.height / scale / zoom + 2 * EDGELESS_BLOCK_CHILD_PADDING;

    if (height >= this._noteFullHeight) {
      return nothing;
    }

    return html`
      <div
        class=${styles.collapsedContent}
        style=${styleMap({
          width: `${width}px`,
          height: `${this._noteFullHeight - height}px`,
          left: `${-(extraPadding + extraBorder / 2)}px`,
          top: `${height + extraPadding + extraBorder / 2}px`,
        })}
      ></div>
    `;
  }

  private _handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowUp') {
      this._docTitle?.inlineEditor?.focusEnd();
    }
  }

  private _hovered() {
    if (
      this.selection.value.some(
        sel => sel.type === 'surface' && sel.blockId === this.model.id
      )
    ) {
      if (this._hoverTimeout) {
        clearTimeout(this._hoverTimeout);
        this._hoverTimeout = null;
      }
      this._isHover = true;
    }
  }

  private _hoverTimeout: ReturnType<typeof setTimeout> | null = null;

  private _leaved(e: MouseEvent) {
    if (this._hoverTimeout) {
      clearTimeout(this._hoverTimeout);
      this._hoverTimeout = null;
    }
    const rect = this.getBoundingClientRect();
    const threshold = -10;
    const leavedFromBottom =
      e.clientY - rect.bottom > threshold &&
      rect.left < e.clientX &&
      e.clientX < rect.right;

    if (leavedFromBottom) {
      this._hoverTimeout = setTimeout(() => {
        this._isHover = false;
      }, 300);
    } else {
      this._isHover = false;
    }
  }

  private _setCollapse(event: MouseEvent) {
    event.stopImmediatePropagation();
    if (this.model.store.readonly) return;

    const { collapse, collapsedHeight } = this.model.props.edgeless;

    if (collapse) {
      this.model.store.updateBlock(this.model, () => {
        this.model.props.edgeless.collapse = false;
      });
    } else if (collapsedHeight) {
      const { edgeless } = this.model.props;
      const bound = getPersistedNoteBound(this.model);
      bound.h = collapsedHeight * (edgeless.scale ?? 1);
      this.model.store.updateBlock(this.model, () => {
        this.model.props.edgeless.collapse = true;
        this.model.props.xywh = bound.serialize();
      });
    }

    this.selection.clear();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.dataset.colourIsland = 'document';

    const selection = this.gfx.selection;

    this._editing = selection.has(this.model.id) && selection.editing;
    this._disposables.add(
      selection.slots.updated.subscribe(() => {
        if (selection.has(this.model.id) && selection.editing) {
          this._editing = true;
        } else {
          this._editing = false;
        }
      })
    );

    this.disposables.addFromEvent(this, 'keydown', this._handleKeyDown);
  }

  override disconnectedCallback() {
    this._setLiveRenderingTransientXYWH(null);
    super.disconnectedCallback();
    if (this._hoverTimeout) {
      clearTimeout(this._hoverTimeout);
      this._hoverTimeout = null;
    }
  }

  get edgelessSlots() {
    return this.std.get(EdgelessLegacySlotIdentifier);
  }

  override firstUpdated() {
    const { _disposables } = this;
    const selection = this.gfx.selection;

    _disposables.add(
      this.edgelessSlots.elementResizeStart.subscribe(() => {
        if (selection.selectedElements.includes(this.model)) {
          this._isResizing = true;
        }
      })
    );

    _disposables.add(
      this.edgelessSlots.elementResizeEnd.subscribe(() => {
        this._isResizing = false;
      })
    );

    _disposables.add(this._followPersistedXYWH());

    const syncLiveRenderingHeightFromMeasuredContent = (
      contentHeight: number
    ) => {
      const scale = this.model.props.edgeless.scale ?? 1;
      this._noteFullHeight = contentHeight + 2 * EDGELESS_BLOCK_CHILD_PADDING;
      this._syncLiveRenderingHeight(this._noteFullHeight * scale);
    };

    const syncPendingEmbeddedLayout = (scheduleUpdate: boolean) => {
      const pending = hasPendingEmbeddedNoteLayout(this);
      const shouldRequestUpdate =
        pending !== this._hasPendingEmbeddedLayout ||
        !this._embeddedLayoutDomSynced;

      this._embeddedLayoutDomSynced = true;
      this._hasPendingEmbeddedLayout = pending;

      if (!shouldRequestUpdate) {
        return;
      }
      if (!scheduleUpdate) {
        this._applyRenderingRect(
          !this.model.props.edgeless.collapse && pending
        );
        return;
      }
      this.requestUpdate();
    };

    const contentResizeObserver = new ResizeObserver(entries => {
      measureGfxPerformance(
        this.std,
        'note-content-resize',
        () => {
          for (const entry of entries) {
            syncLiveRenderingHeightFromMeasuredContent(
              entry.contentRect.height
            );
          }
        },
        () => entries.length,
        () => ({ blockId: this.model.id })
      );
    });

    const observer = new MutationObserver(records => {
      measureGfxPerformance(
        this.std,
        'note-content-mutation',
        () => {
          const rect = this._noteContent?.getBoundingClientRect();
          if (!rect) return;
          const zoom = this.gfx.viewport.zoom;
          const scale = this.model.props.edgeless.scale ?? 1;
          this._noteFullHeight =
            rect.height / scale / zoom + 2 * EDGELESS_BLOCK_CHILD_PADDING;
          this._syncLiveRenderingHeight(this._noteFullHeight * scale);
          syncPendingEmbeddedLayout(true);
        },
        () => records.length,
        () => ({ blockId: this.model.id })
      );
    });
    if (this._noteContent) {
      contentResizeObserver.observe(this._noteContent);
      observer.observe(this, {
        attributeFilter: [
          DATABASE_INITIAL_LOAD_READY_ATTR,
          STREAMLAIN_NOTE_LAYOUT_READY_ATTR,
        ],
        attributes: true,
        childList: true,
        subtree: true,
      });
      const rect = this._noteContent.getBoundingClientRect();
      const zoom = this.gfx.viewport.zoom;
      const scale = this.model.props.edgeless.scale ?? 1;
      syncLiveRenderingHeightFromMeasuredContent(rect.height / scale / zoom);
      _disposables.add(() => observer.disconnect());
      _disposables.add(() => contentResizeObserver.disconnect());
      syncPendingEmbeddedLayout(false);
    }
  }

  override updated(changedProperties: PropertyValues) {
    measureGfxPerformance(
      this.std,
      'note-updated',
      () => {
        if (changedProperties.has('_editing') && this._editing) {
          this.std
            .getOptional(TelemetryProvider)
            ?.track('EdgelessNoteEditing', {
              page: 'edgeless',
              segment: this.model.isPageBlock() ? 'page' : 'note',
            });
        }
      },
      () => changedProperties.size,
      () => ({
        blockId: this.model.id,
        changedProperties: [...changedProperties.keys()]
          .map(property => String(property))
          .sort()
          .join(','),
      })
    );
  }

  override getRenderingRect() {
    const { edgeless } = this.model.props;
    const { scale = 1 } = edgeless;

    const bound = Bound.deserialize(
      this.model.transientXYWH ?? this.model.props.xywh
    );
    const width = bound.w / scale;
    const height = bound.h / scale;

    return {
      x: bound.x,
      y: bound.y,
      w: width,
      h: height,
      zIndex: this.toZIndex(),
    };
  }

  private _getPendingEmbeddedLayoutForRender() {
    return (
      this._hasPendingEmbeddedLayout ||
      (!this._embeddedLayoutDomSynced &&
        hasPotentialPendingEmbeddedNoteLayoutModel(this.model))
    );
  }

  private _emitLiveRenderingTransientGeometryUpdated() {
    this.gfx.grid.update(this.model);
    this.gfx.selection.slots.transientGeometryUpdated.next(this.model.id);
  }

  private _setLiveRenderingTransientXYWH(nextXYWH: SerializedXYWH | null) {
    const previousXYWH = this._liveRenderingTransientXYWH;
    this._liveRenderingTransientXYWH = nextXYWH;

    if (!nextXYWH) {
      if (previousXYWH && this.model.transientXYWH === previousXYWH) {
        this.model.transientXYWH = undefined;
        this.refreshGfxTransform();
        this._emitLiveRenderingTransientGeometryUpdated();
        this.requestUpdate();
      }
      return;
    }

    const currentXYWH = this.model.transientXYWH;
    if (currentXYWH === nextXYWH) {
      return;
    }

    if (currentXYWH && currentXYWH !== previousXYWH) {
      return;
    }

    this.model.transientXYWH = nextXYWH;
    this.refreshGfxTransform();
    this._emitLiveRenderingTransientGeometryUpdated();
    this.requestUpdate();
  }

  /**
   * The measured height paints at the position it was measured at. Undo, a
   * collaborator or any other writer can change the stored position without a
   * drag; the note follows it, otherwise it keeps painting at the old spot.
   */
  private _followPersistedXYWH() {
    return this.model.propsUpdated.subscribe(({ key }) => {
      if (key === 'xywh') this._rebaseLiveRenderingTransientXYWH();
    });
  }

  private _rebaseLiveRenderingTransientXYWH() {
    if (!this._liveRenderingTransientXYWH) {
      return;
    }

    if (this.model.props.edgeless.collapse) {
      this._setLiveRenderingTransientXYWH(null);
      return;
    }

    const currentXYWH = this.model.transientXYWH;
    if (currentXYWH && currentXYWH !== this._liveRenderingTransientXYWH) {
      return;
    }

    const baseBound = Bound.deserialize(this.model.props.xywh);
    const liveBound = Bound.deserialize(this._liveRenderingTransientXYWH);
    liveBound.x = baseBound.x;
    liveBound.y = baseBound.y;
    liveBound.w = baseBound.w;
    this._setLiveRenderingTransientXYWH(liveBound.serialize());
  }

  override refreshGfxTransform() {
    // Remote movement temporarily owns transientXYWH. When it releases that
    // geometry, restore the measured content height at the final document
    // position; the intrinsic content may not resize again after preview clear.
    if (!this.model.transientXYWH) {
      this._rebaseLiveRenderingTransientXYWH();
    }
    super.refreshGfxTransform();
  }

  private readonly _syncLiveRenderingHeight = (height: number | null) => {
    if (
      height === null ||
      !Number.isFinite(height) ||
      height <= 0 ||
      this.model.props.edgeless?.collapse
    ) {
      this._setLiveRenderingTransientXYWH(null);
      return;
    }

    const bound = Bound.deserialize(this.model.props.xywh);
    const scale = this.model.props.edgeless?.scale ?? 1;
    const liveHeight = Math.max(height, NOTE_MIN_HEIGHT * scale);

    if (almostEqual(bound.h, liveHeight, 2)) {
      this._setLiveRenderingTransientXYWH(null);
      return;
    }

    bound.h = liveHeight;
    this._setLiveRenderingTransientXYWH(bound.serialize());
  };

  private readonly _baseOnDragMove = this.onDragMove;

  override onDragMove = (context: DragMoveContext) => {
    this._baseOnDragMove(context);
    this._rebaseLiveRenderingTransientXYWH();
  };

  override onDragEnd(context: DragEndContext) {
    super.onDragEnd(context);
    this._rebaseLiveRenderingTransientXYWH();
  }

  private _applyRenderingRect(lockHeight: boolean) {
    const { x, y, w, h, zIndex } = this.getRenderingRect();

    this.style.left = `${x}px`;
    this.style.top = `${y}px`;
    this.style.width = `${w}px`;
    // Keep the DOM owner aligned with the same live geometry used by the GFX
    // grid and selection layer. Leaving an expanded note at `height: unset`
    // collapses the absolutely positioned owner to zero while its page,
    // database, and rich-text descendants continue painting as overflow. That
    // prevents Chromium from treating the note as one stable paint surface on
    // every viewport transform.
    this.style.height = `${h}px`;
    this.style.zIndex = zIndex;
    this.dataset.streamlainNoteLayoutLocked = lockHeight ? 'true' : 'false';
  }

  override renderBlock(): ReturnType<NoteBlockComponent['renderBlock']> {
    return measureGfxPerformance(
      this.std,
      'note-render',
      () => {
        const { collapse } = this.model.props.edgeless;
        const lockHeight =
          !collapse && this._getPendingEmbeddedLayoutForRender();
        this._applyRenderingRect(lockHeight);

        return this.renderGfxBlock() as ReturnType<
          NoteBlockComponent['renderBlock']
        >;
      },
      undefined,
      () => ({
        blockId: this.model.id,
        collapsed: Boolean(this.model.props.edgeless.collapse),
        heightLocked: this.dataset.streamlainNoteLayoutLocked === 'true',
      })
    );
  }

  override renderGfxBlock() {
    const { model } = this;
    const { displayMode } = model.props;
    if (!!displayMode && displayMode === NoteDisplayMode.DocOnly)
      return nothing;

    const { xywh, edgeless } = model.props;
    const { borderRadius } = resolveEdgelessNoteStyle(edgeless);
    const { collapse = false, collapsedHeight, scale = 1 } = edgeless ?? {};

    const { tool } = this.gfx;

    const bound = Bound.deserialize(xywh);
    const height = bound.h / scale;

    const style = {
      borderRadius: borderRadius + 'px',
      transform: `scale(${scale})`,
    };

    const extra = this._editing ? ACTIVE_NOTE_EXTRA_PADDING : 0;

    const isCollapsable =
      collapse != null &&
      collapsedHeight != null &&
      collapsedHeight !== this._noteFullHeight;

    const isCollapseArrowUp = collapse
      ? this._noteFullHeight < height
      : !!collapsedHeight && collapsedHeight < height;

    const hasHeader = !!this.std.getOptional(NoteConfigExtension.identifier)
      ?.edgelessNoteHeader;

    return html`
      <div
        class=${styles.edgelessNoteContainer}
        style=${styleMap(style)}
        data-model-height="${bound.h}"
        data-model-xywh="${xywh}"
        data-editing=${this._editing}
        data-collapse=${ifDefined(collapse)}
        data-testid="edgeless-note-container"
        @mouseleave=${this._leaved}
        @mousemove=${this._hovered}
        data-scale="${scale}"
      >
        <edgeless-note-background
          .editing=${this._editing}
          .note=${this.model}
        ></edgeless-note-background>

        <div
          data-testid="edgeless-note-clip-container"
          class=${styles.clipContainer}
          style=${styleMap({
            'overflow-y': this._isShowCollapsedContent ? 'initial' : 'clip',
          })}
        >
          <div>
            <edgeless-page-block-title
              .note=${this.model}
            ></edgeless-page-block-title>
            <div
              contenteditable=${String(!this.store.readonly$.value)}
              class="edgeless-note-page-content"
            >
              ${this.renderPageContent()}
            </div>
          </div>
        </div>

        <edgeless-note-mask
          .model=${this.model}
          .host=${this.host}
          .zoom=${this.gfx.viewport.zoom ?? 1}
          .disableMask=${this.hideMask}
          .editing=${this._editing}
        ></edgeless-note-mask>

        ${isCollapsable &&
        tool.currentToolName$.value !== 'frameNavigator' &&
        (!this.model.isPageBlock() || !hasHeader)
          ? html`<div
              class="${classMap({
                [styles.collapseButton]: true,
                flip: isCollapseArrowUp,
              })}"
              style=${styleMap({
                bottom: this._editing ? `${-extra}px` : '0',
              })}
              data-testid="edgeless-note-collapse-button"
              @mousedown=${stopPropagation}
              @mouseup=${stopPropagation}
              @click=${this._setCollapse}
            >
              ${MoreIndicator}
            </div>`
          : nothing}
        ${this._collapsedContent()}
      </div>
    `;
  }

  override onBoxSelected(_: BoxSelectionContext) {
    return this.model.props.displayMode !== NoteDisplayMode.DocOnly;
  }

  @state()
  private accessor _editing = false;

  @state()
  private accessor _isHover = false;

  @state()
  private accessor _isResizing = false;

  @state()
  private accessor _noteFullHeight = 0;

  @state()
  accessor hideMask = false;

  @query(`.${styles.clipContainer} > div`)
  private accessor _noteContent: HTMLElement | null = null;

  @query('doc-title')
  private accessor _docTitle: DocTitle | null = null;
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_EDGELESS_NOTE]: EdgelessNoteBlockComponent;
  }
}

export const EdgelessNoteInteraction =
  GfxViewInteractionExtension<EdgelessNoteBlockComponent>(
    NoteBlockSchema.model.flavour,
    {
      selectionBorderRadius: ({ model }) =>
        resolveEdgelessNoteStyle(model.props.edgeless).borderRadius *
        (model.props.edgeless.scale ?? 1),
      handleRotate: () => {
        return {
          beforeRotate(context) {
            context.set({
              rotatable: false,
            });
          },
        };
      },
      handleResize: ({ model }) => {
        const initialScale: number = model.props.edgeless.scale ?? 1;
        return {
          onResizeStart(context): void {
            context.default(context);
            model.stash('edgeless');
          },

          onResizeMove(context): void {
            if (model.store.readonly) return;

            const { originalBound, newBound, lockRatio, constraint } = context;
            const { minWidth, minHeight, maxHeight, maxWidth } = constraint;

            let scale = initialScale;
            let edgelessProp = { ...model.props.edgeless };
            const originalRealWidth = originalBound.w / scale;

            if (lockRatio) {
              scale = newBound.w / originalRealWidth;
              edgelessProp.scale = scale;
            }

            newBound.w = clamp(newBound.w, minWidth * scale, maxWidth);
            newBound.h = clamp(newBound.h, minHeight * scale, maxHeight);

            if (newBound.h > minHeight * scale) {
              edgelessProp.collapse = true;
              edgelessProp.collapsedHeight = newBound.h / scale;
            }

            model.props.edgeless = edgelessProp;
            model.props.xywh = newBound.serialize();
          },

          onResizeEnd(context): void {
            context.default(context);
            model.pop('edgeless');
          },
        };
      },
      handleSelection: ({ std, gfx, view, model }) => {
        return {
          onSelect(context) {
            const { selected, multiSelect, event: e } = context;
            const { editing } = gfx.selection;
            const alreadySelected = gfx.selection.has(model.id);

            if (!multiSelect && selected && (alreadySelected || editing)) {
              if (model.isLocked()) return;

              if (alreadySelected && editing) {
                return;
              }

              gfx.selection.set({
                elements: [model.id],
                editing: true,
              });

              view.updateComplete
                .then(() => {
                  if (!view.isConnected) {
                    return;
                  }

                  if (model.children.length === 0) {
                    const blockId = std.store.addBlock(
                      'affine:paragraph',
                      { type: 'text' },
                      model.id
                    );

                    if (blockId) {
                      focusTextModel(std, blockId);
                    }
                  } else {
                    const rect = view
                      .querySelector('.affine-block-children-container')
                      ?.getBoundingClientRect();

                    if (rect) {
                      const offsetY = 8 * gfx.viewport.zoom;
                      const offsetX = 2 * gfx.viewport.zoom;
                      const x = clamp(
                        e.clientX,
                        rect.left + offsetX,
                        rect.right - offsetX
                      );
                      const y = clamp(
                        e.clientY,
                        rect.top + offsetY,
                        rect.bottom - offsetY
                      );
                      handleNativeRangeAtPoint(x, y);
                    } else {
                      handleNativeRangeAtPoint(e.clientX, e.clientY);
                    }
                  }
                })
                .catch(console.error);
            } else {
              context.default(context);
            }
          },
        };
      },
    }
  );
